import dayjs from 'dayjs'

import { get, post, put } from './requests'

const TWO_DAYS_AGO = dayjs().subtract(2, 'day')
const ONE_YEAR_AGO = dayjs().subtract(1, 'year')
export const RANGE_TYPES = {
  CUSTOM: 'custom_range',
  PAST_12: 'past_12_months',
}

const formatEntities = (entities) =>
  entities.map((entity) => entity.replaceAll(' ', '_').toLowerCase())

const formatDate = (_dayjs) => _dayjs.format('MM/DD/YYYY') // format accepted by backend

const keepInRange = (_date) => {
  const date = dayjs(_date)
  return date.isBefore(ONE_YEAR_AGO)
    ? ONE_YEAR_AGO
    : date.isAfter(TWO_DAYS_AGO)
    ? TWO_DAYS_AGO
    : date
}

const getStartEnd = (timeRange) =>
  timeRange
    ? timeRange?.type === RANGE_TYPES.CUSTOM
      ? {
          start: formatDate(keepInRange(timeRange.beginning)),
          end: formatDate(keepInRange(timeRange.end)),
        }
      : {
          start: formatDate(ONE_YEAR_AGO),
          end: formatDate(TWO_DAYS_AGO),
        }
    : {}

const formatPayload = ({ audienceId, timeRange, entities, kecc, keccType, term }) => {
  let additionalFields = {}
  if (entities && Array.isArray(entities)) {
    additionalFields.entities = formatEntities(entities)
  }
  if (kecc && Array.isArray(kecc)) {
    additionalFields.kecc = formatEntities(kecc)
  }
  if (keccType) {
    additionalFields.field = keccType
  }
  if (term) {
    additionalFields.term = term
  }

  return {
    filters: [
      {
        sources: ['sharethis'],
        key: 'audience',
        operator: 'equals',
        value: audienceId,
      },
    ],
    ...getStartEnd(timeRange),
    ...additionalFields,
  }
}

export const getReport = ({ audienceId }) =>
  get(['report', 'v2'], { transformPayload: false }, { audience_id: audienceId })

export const createReport = ({ audienceId, slides, audienceName }) =>
  post(
    ['report', 'v2'],
    { transformPayload: false },
    { id: audienceId, slides, audience_name: audienceName }
  )

export const updateReport = ({ audienceId, slides, audienceName }) =>
  put(
    ['report', 'v2'],
    { transformPayload: false },
    { id: audienceId, slides, audience_name: audienceName }
  )

export const getDeviceUsage = (payload) =>
  post(['data-explorer', 'insights', 'device-usage'], {}, formatPayload(payload))

export const getSocialDistribution = (payload) =>
  post(['data-explorer', 'insights', 'social-distribution'], {}, formatPayload(payload))

export const getStatesOverindex = (payload) =>
  post(
    ['data-explorer', 'insights', 'states-overindex'],
    { transformPayload: false },
    formatPayload(payload)
  )

export const getEntityDistribution = (payload) =>
  post(
    ['data-explorer', 'insights', 'entity-distribution'],
    { transformPayload: false },
    formatPayload(payload)
  )

export const getEntitySentiment = (payload) =>
  post(
    ['data-explorer', 'insights', 'entity-sentiment'],
    { transformPayload: false },
    formatPayload(payload)
  )

export const getCategoryOverindex = (payload) =>
  post(
    ['data-explorer', 'insights', 'category-overindex'],
    { transformPayload: false },
    formatPayload(payload)
  )

export const getEventTrends = (payload) =>
  post(
    ['data-explorer', 'insights', 'event-trends'],
    { transformPayload: false },
    formatPayload(payload)
  )

export const getKECCTrends = (payload) =>
  post(
    ['data-explorer', 'insights', 'kecc-trends'],
    { transformPayload: false },
    formatPayload(payload)
  )

export const getTrendingTopics = ({ audienceId, lookback, terms }) =>
  post(
    ['data-explorer', 'term-trends'],
    { transformPayload: false },
    {
      filters: [
        {
          key: 'audience',
          operator: 'equals',
          value: audienceId,
          sources: ['sharethis'],
        },
      ],
      field: 'keywords',
      lookback,
      terms,
    }
  )

export const getIncomeDistribution = (payload) =>
  post(
    ['data-explorer', 'insights', 'income-distribution'],
    { transformPayload: false },
    formatPayload(payload)
  )

export const getGenderDistribution = (payload) =>
  post(
    ['data-explorer', 'insights', 'gender-distribution'],
    { transformPayload: false },
    formatPayload(payload)
  )

export const getAgeDistribution = (payload) =>
  post(
    ['data-explorer', 'insights', 'age-distribution'],
    { transformPayload: false },
    formatPayload(payload)
  )

export const getIncomeByAge = (payload) =>
  post(
    ['data-explorer', 'insights', 'age-by-income'],
    { transformPayload: false },
    formatPayload(payload)
  )

export const getIncomeByGender = (payload) =>
  post(
    ['data-explorer', 'insights', 'income-by-gender'],
    { transformPayload: false },
    formatPayload(payload)
  )

// payload: {term, audienceId}
export const searchTopic = (payload) =>
  post(['data-explorer', 'auto-complete'], { transformPayload: false }, formatPayload(payload))
